<template>
<sidebar-widget
    :start-expanded="true"
    :sections="sections"
    :headers="[{label: 'Loan'}, {label: 'Balance', classes: 'cw-6'}, {label: 'Repayment', classes: 'cw-10'}]"
>
    <template v-slot:title>Retirement Plan Loans</template>

    <template v-slot:headerbutton>
        <!-- NB: the employer only administers repayment for employees who hold a
             deferred comp plan with them, so with no enrollment there is nothing to
             take a loan against. An IRA-based plan cannot lend at all, which is what
             `supports_loans` filters out. -->
        <button v-if="hasLoanEligiblePlan" class="btn btn-outline-primary" @click.prevent.stop="addModalOpen = true"><i class="far fa-plus"></i> Add</button>
        <span v-else class="text-muted"><small>No plan that can lend</small></span>
    </template>

    <template v-slot:row-details="props">
        <div>
            <span>Plan: </span>
            <span>{{ props.row.obj.employee_deferred_comp?.deferred_comp_plan?.display_name }}</span>
            <br />
            <span>Began: </span>
            <span>{{ $filters.date(props.row.obj.created_on) }}</span>
            <br />
            <span>Added by: {{ props.row.obj.created_by?.email }}</span>
            <br />
            <span>Original Principal: {{ $filters.dollars(props.row.obj.amount) }}</span>
            <br />
            <span>Repaid to Date: {{ $filters.dollars(props.row.obj.amount_paid) }}</span>
            <br />
            <span>Outstanding Balance: {{ $filters.dollars(props.row.obj.current_balance) }}</span>
            <br />
            <span>Repayment per Pay Period: {{ $filters.dollars(props.row.obj.max_paycheck_amount) }}</span>

            <p v-if="props.row.obj.is_closed" class="mt-hf mb-0"><small class="text-muted">Paid off. No further repayments are withheld.</small></p>

            <button-row v-if="props.row.obj.is_active">
                <button class="btn btn-outline-primary" @click.prevent="removeLoan(props.row.obj.id)"><i class="far fa-trash"></i> Delete</button>

                <button class="btn btn-outline-primary" @click.prevent="currentLoanId = props.row.obj.id; editModalOpen = true"><i class="far fa-pen"></i> Edit</button>
            </button-row>
        </div>
    </template>

    <template v-slot:extra>
        <deductions-modal
            v-if="addModalOpen || editModalOpen"
            :deduction="currentLoan"
            :client="client"
            :employee="employee"
            :deferred-comps="loanEligibleDeferredComps"
            :label-overrides="labelOverrides"
            @close="currentLoanId = null; addModalOpen = editModalOpen = false"
            @updated="$emit('updated'); getData()"
        />
    </template>
</sidebar-widget>

</template>

<script>
import SidebarWidget from '@/components/SidebarWidget'
import ButtonRow from '@/components/ButtonRow'
import DeductionsModal from './MiscDeductionsModal'

export default {
    // NB: no clientDeductionTypes. The deduction type a repayment is withheld under
    // comes from the plan the loan was drawn against, so there is no list to pick from.
    props: ['employee', 'client', ],
    components: {SidebarWidget, ButtonRow, DeductionsModal, },
    computed: {
        // NB: no deductionType label. The loan form does not show the deduction type at
        // all -- it comes from the plan, so there is nothing to label.
        labelOverrides() {
            return {
                noun: 'Retirement Plan Loan',
                startingAmount: 'Principal',
                perPaycheckAmount: 'Repayment',
            }
        },
        loans() {
            return this.employee.misc_deductions.filter(d => {
                return d.company_deduction_type.deduction_type.category == 'retirement-loan'
            })
        },
        loanEligibleDeferredComps() {
            return (this.employee.deferred_comps || []).filter(dc => {
                return dc.is_active && dc.deferred_comp_plan?.supports_loans
            })
        },
        hasLoanEligiblePlan() {
            return this.loanEligibleDeferredComps.length > 0
        },
        sections() {
            const activeRows = []
            const inactiveRows = []

            this.loans.forEach(d => {
                let row = {
                    obj: d,
                    cells: [
                        d.company_deduction_type.name,
                        this.$filters.dollars(d.current_balance),
                        this.$filters.dollars(d.max_paycheck_amount) + '/pay period',
                    ],
                    id: d.id,
                }
                if (d.is_active) {
                    activeRows.push(row)
                } else {
                    inactiveRows.push(row)
                }
            })

            return [
                {title: 'Active', rows: activeRows, startExpanded: true},
                {title: 'Inactive', rows: inactiveRows, startExpanded: false},
            ]
        },
        currentLoan() {
            if (this.currentLoanId) {
                return this.loans.find(l => l.id == this.currentLoanId)
            }
            return undefined
        },
    },
    data() {
        return {
            addModalOpen: false,
            editModalOpen: false,
            currentLoanId: null,
        }
    },
    mounted() {
        this.getData(true)
    },
    methods: {
        removeLoan(loanId) {
            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                const data = {
                    active_status: 'inactive',
                }

                // NB: the misc deduction endpoints. A loan is an EmployeeMiscDeduction.
                return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/misc-deductions/${loanId}`, data).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully removed loan.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        getData(silent) {}
    },
}
</script>
